import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraDivider } from '@mono/ui-components';
import { BookOpen, ChevronDown, ChevronRight, ClipboardCheck, ClipboardList, FileSpreadsheet, FileText, History, LayoutDashboard, Library, Link2, MessageSquare, Settings, ShieldCheck, Sparkles, Target, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlagsReady } from '../contexts/featureFlagContext';
import { useProjectVolumes } from '../hooks/useProjectVolumes';
import { cn } from '../lib/utils';
import { useWorkspace } from './ProposalDashboard/v2/workspace/WorkspaceContext';
import { WorkspaceSwitcher } from './ProposalDashboard/v2/workspace/WorkspaceSwitcher';
function NavItem({ href, label, icon, isCollapsed }) {
    const location = useLocation();
    const isActive = location.pathname === href || href !== '/' && location.pathname.startsWith(href) && !location.pathname.slice(href.length).includes('/');
    return /*#__PURE__*/ _jsx(Link, {
        to: href,
        title: label,
        "aria-current": isActive ? 'page' : undefined,
        className: "block min-w-0",
        children: /*#__PURE__*/ _jsxs(SpecraButton, {
            variant: "ghost",
            className: cn('w-full min-w-0 justify-start gap-3 font-medium', isActive ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground'),
            children: [
                icon,
                /*#__PURE__*/ _jsx("span", {
                    className: cn('min-w-0 flex-1 truncate text-left transition-opacity', isCollapsed ? 'w-0 opacity-0' : 'opacity-100'),
                    children: label
                })
            ]
        })
    });
}
function SectionLabel({ label, isCollapsed, collapsible, expanded, onToggle }) {
    if (isCollapsed) return null;
    const baseClass = 'flex w-full items-center gap-1 px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70';
    if (collapsible) {
        return /*#__PURE__*/ _jsxs("button", {
            type: "button",
            onClick: onToggle,
            className: cn(baseClass, 'hover:text-muted-foreground transition-colors'),
            "aria-expanded": expanded,
            children: [
                expanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                    className: "h-3 w-3",
                    "aria-hidden": "true"
                }) : /*#__PURE__*/ _jsx(ChevronRight, {
                    className: "h-3 w-3",
                    "aria-hidden": "true"
                }),
                /*#__PURE__*/ _jsx("span", {
                    children: label
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: baseClass,
        children: label
    });
}
const VOLUMES_DEFAULT_VISIBLE = 5;
/**
 * Picks the rail icon for a project volume based on its derived workspace
 * kind. Custom volumes get a violet sparkle to distinguish them from
 * pipeline-derived ones; the rest are differentiated by file type.
 */ function volumeIcon(volume) {
    if (volume.isCustom) {
        return /*#__PURE__*/ _jsx(Sparkles, {
            className: "w-4 h-4 flex-shrink-0 text-violet-500",
            "aria-hidden": "true"
        });
    }
    switch(volume.kind){
        case 'workbook':
            return /*#__PURE__*/ _jsx(FileSpreadsheet, {
                className: "w-4 h-4 flex-shrink-0",
                "aria-hidden": "true"
            });
        case 'template':
            return /*#__PURE__*/ _jsx(ClipboardList, {
                className: "w-4 h-4 flex-shrink-0",
                "aria-hidden": "true"
            });
        default:
            return /*#__PURE__*/ _jsx(BookOpen, {
                className: "w-4 h-4 flex-shrink-0",
                "aria-hidden": "true"
            });
    }
}
/**
 * Navigation items for project mode (project-scoped views).
 *
 * Two render modes:
 *  - Legacy (default for /project/ and /proposal/ when V2 flag is off):
 *    Overview, Chat, Documents, Team, Settings + global Opportunities/SharePoint.
 *  - V2 (proposal only, when `proposal_dashboard_v2` Flipt flag is on):
 *    WORKSPACES (Home, Compliance) + VOLUMES placeholder + SOURCES (Files, CLINs, ...) +
 *    global items below. Mirrors the V2 dashboard rail design.
 */ export function ProjectNavItems({ isCollapsed, projectId, isProposal }) {
    // Use the correct route prefix based on project type
    const routePrefix = isProposal ? 'proposal' : 'project';
    const dashboardV2Flag = useFeatureFlag('proposal_dashboard_v2');
    const dashboardV2Enabled = dashboardV2Flag ?? true; // default ON
    const useV2 = !!isProposal && dashboardV2Enabled;
    const flagsReady = useFeatureFlagsReady();
    const specraRedesignEnabled = useFeatureFlag('specra_redesign_dashboard');
    const specraRedesign = flagsReady && specraRedesignEnabled;
    // Hook order is fixed regardless of mode — when not in V2 the query is
    // skipped via the `enabled` flag inside the hook.
    const volumesQuery = useProjectVolumes(useV2 ? projectId : undefined);
    const volumes = volumesQuery.data ?? [];
    // Local UI state for the V2 rail collapsibles. Volumes show top N by
    // default with a "View N more" toggle; sources are collapsible and
    // default open. Hooks are unconditional to keep render order stable.
    const [volumesExpanded, setVolumesExpanded] = useState(false);
    const [sourcesOpen, setSourcesOpen] = useState(true);
    const { workspace, setWorkspace, showSource } = useWorkspace(projectId);
    // Global items accessible within project context (always shown at the bottom)
    const globalNavItems = [
        {
            href: '/opportunities',
            label: 'Opportunities',
            icon: /*#__PURE__*/ _jsx(Target, {
                className: "w-4 h-4 flex-shrink-0"
            })
        },
        {
            href: '/sharepoint-documents',
            label: 'SharePoint',
            icon: /*#__PURE__*/ _jsx(Link2, {
                className: "w-4 h-4 flex-shrink-0"
            })
        }
    ];
    if (useV2 && projectId) {
        // V2 proposal dashboard rail
        const workspaceItems = [
            {
                href: `/proposal/${projectId}/v2/home`,
                label: 'Home',
                icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/compliance`,
                label: 'Compliance',
                icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/capability-matrix`,
                label: 'Capability Matrix',
                icon: /*#__PURE__*/ _jsx(ShieldCheck, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/chat-history`,
                label: 'Chat History',
                icon: /*#__PURE__*/ _jsx(MessageSquare, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            }
        ];
        const sourceItems = [
            {
                href: `/proposal/${projectId}/v2/sources/files`,
                label: 'Files',
                icon: /*#__PURE__*/ _jsx(FileText, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/sources/clins`,
                label: 'CLINs',
                icon: /*#__PURE__*/ _jsx(Library, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/sources/lcats`,
                label: 'Labor Categories',
                icon: /*#__PURE__*/ _jsx(Users, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/sources/pricing`,
                label: 'Pricing',
                icon: /*#__PURE__*/ _jsx(Library, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/sources/team`,
                label: 'Team',
                icon: /*#__PURE__*/ _jsx(Users, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            },
            {
                href: `/proposal/${projectId}/v2/sources/history`,
                label: 'Amendment History',
                icon: /*#__PURE__*/ _jsx(History, {
                    className: "w-4 h-4 flex-shrink-0"
                })
            }
        ];
        return /*#__PURE__*/ _jsxs("nav", {
            className: "flex-1 min-h-0 p-2 space-y-1 overflow-x-hidden overflow-y-auto",
            "aria-label": "Proposal V2 navigation",
            children: [
                !isCollapsed && /*#__PURE__*/ _jsx("div", {
                    className: "px-2 pb-2",
                    children: /*#__PURE__*/ _jsx(WorkspaceSwitcher, {
                        current: workspace,
                        onSelect: setWorkspace
                    })
                }),
                /*#__PURE__*/ _jsx(SectionLabel, {
                    label: "Workspaces",
                    isCollapsed: isCollapsed
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "space-y-1",
                    children: workspaceItems.map((item)=>/*#__PURE__*/ _jsx(NavItem, {
                            ...item,
                            isCollapsed: isCollapsed
                        }, item.href))
                }),
                /*#__PURE__*/ _jsx(SectionLabel, {
                    label: "Volumes",
                    isCollapsed: isCollapsed
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "space-y-1",
                    children: volumesQuery.isLoading && volumes.length === 0 ? /*#__PURE__*/ _jsx("div", {
                        className: cn('px-3 py-2 text-xs italic text-muted-foreground/60', isCollapsed && 'hidden'),
                        children: "Loading..."
                    }) : volumes.length === 0 ? /*#__PURE__*/ _jsx("div", {
                        className: cn('px-3 py-2 text-xs italic text-muted-foreground/70', isCollapsed && 'hidden'),
                        children: "No volumes yet"
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            (volumesExpanded ? volumes : volumes.slice(0, VOLUMES_DEFAULT_VISIBLE)).map((v)=>/*#__PURE__*/ _jsx(NavItem, {
                                    href: `/proposal/${projectId}/v2/volume/${v.volumeId}`,
                                    label: v.displayName,
                                    icon: volumeIcon(v),
                                    isCollapsed: isCollapsed
                                }, v.volumeId)),
                            volumes.length > VOLUMES_DEFAULT_VISIBLE && !isCollapsed && /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setVolumesExpanded((v)=>!v),
                                className: "flex w-full items-center gap-1 px-3 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors",
                                children: volumesExpanded ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(ChevronDown, {
                                            className: "h-3 w-3",
                                            "aria-hidden": "true"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Show less"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(ChevronRight, {
                                            className: "h-3 w-3",
                                            "aria-hidden": "true"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Show ",
                                                volumes.length - VOLUMES_DEFAULT_VISIBLE,
                                                " more"
                                            ]
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SectionLabel, {
                    label: "Sources",
                    isCollapsed: isCollapsed,
                    collapsible: true,
                    expanded: sourcesOpen,
                    onToggle: ()=>setSourcesOpen((v)=>!v)
                }),
                sourcesOpen && /*#__PURE__*/ _jsx("div", {
                    className: "space-y-1",
                    children: sourceItems.filter((item)=>{
                        // Extract the source key from the href (last segment).
                        const segments = item.href.split('/');
                        const sourceKey = segments[segments.length - 1];
                        return showSource(sourceKey);
                    }).map((item)=>/*#__PURE__*/ _jsx(NavItem, {
                            ...item,
                            isCollapsed: isCollapsed
                        }, item.href))
                }),
                !specraRedesign && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "py-2",
                            children: /*#__PURE__*/ _jsx(SpecraDivider, {})
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "space-y-1",
                            children: globalNavItems.map((item)=>/*#__PURE__*/ _jsx(NavItem, {
                                    ...item,
                                    isCollapsed: isCollapsed
                                }, item.href))
                        })
                    ]
                })
            ]
        });
    }
    // Legacy project nav (used by both /project/ and /proposal/ when flag is off)
    const projectNavItems = [
        {
            href: `/${routePrefix}/${projectId}`,
            label: 'Overview',
            icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
                className: "w-4 h-4 flex-shrink-0"
            })
        },
        {
            href: `/${routePrefix}/${projectId}/chat`,
            label: 'Chat',
            icon: /*#__PURE__*/ _jsx(MessageSquare, {
                className: "w-4 h-4 flex-shrink-0"
            })
        },
        {
            href: `/${routePrefix}/${projectId}/documents`,
            label: 'Documents',
            icon: /*#__PURE__*/ _jsx(FileText, {
                className: "w-4 h-4 flex-shrink-0"
            })
        },
        {
            href: `/${routePrefix}/${projectId}/team`,
            label: 'Team',
            icon: /*#__PURE__*/ _jsx(Users, {
                className: "w-4 h-4 flex-shrink-0"
            })
        },
        {
            href: `/${routePrefix}/${projectId}/settings`,
            label: 'Settings',
            icon: /*#__PURE__*/ _jsx(Settings, {
                className: "w-4 h-4 flex-shrink-0"
            })
        }
    ];
    return /*#__PURE__*/ _jsxs("nav", {
        className: "flex-1 min-h-0 p-2 space-y-1 overflow-x-hidden overflow-y-auto",
        "aria-label": "Project navigation",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-1",
                children: projectNavItems.map((item)=>/*#__PURE__*/ _jsx(NavItem, {
                        ...item,
                        isCollapsed: isCollapsed
                    }, item.href))
            }),
            !specraRedesign && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "py-2",
                        children: /*#__PURE__*/ _jsx(SpecraDivider, {})
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-1",
                        children: globalNavItems.map((item)=>/*#__PURE__*/ _jsx(NavItem, {
                                ...item,
                                isCollapsed: isCollapsed
                            }, item.href))
                    })
                ]
            })
        ]
    });
}
